import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import {
  EXPORT_TYPES,
  REPORT_NAMES,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
  UserDetails,
} from "../../../constants/common";
import { ArrowUpDown, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { APP_ROUTES } from "../../../routes";
import { useNavigate } from "react-router-dom";
import DeleteUserModal from "../../modal/DeleteUserModal";
import toast, { Toaster } from "react-hot-toast";
import { cn } from "../../../utils/cn";
import { ExportUsersForExcelPDFBody } from "../../../hooks/user/interface";
import {
  useExportUsersForExcel,
  useExportUsersForPDF,
} from "../../../hooks/user/mutation";
import { downloadFile } from "../../../lib/functions";
import StatusCodes from "../../../constants/StatusCodes";

interface SearchUsersTableProps {
  allUsers: never[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  exportBody?: ExportUsersForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const SearchUsersTable = ({
  allUsers,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  exportBody,
  pageSize,
  setPageSize,
}: SearchUsersTableProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState(0);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const { mutateAsync: ExportUsersForExcel } = useExportUsersForExcel();
  const { mutateAsync: ExportUsersForPDF } = useExportUsersForPDF();

  const handleDeleteUser = useCallback(async (id: number) => {
    setIsDeleteUserModalOpen(true);
    setSelectedUserId(id);
  }, []);

  const usersColumns: ColumnDef<UserDetails>[] = useMemo(
    () => [
      {
        accessorKey: "fullname",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("fullName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() =>
                navigate(
                  `${
                    APP_ROUTES.ADD_USERS
                  }?ID=${row.original.user_id.toString()}`
                )
              }
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.fullname}
            </span>
          );
        },
      },
      {
        accessorKey: "username",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("username")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          // return <span>{row.original.username}</span>;
          return (
            <span
              onClick={() =>
                navigate(
                  `${
                    APP_ROUTES.ADD_USERS
                  }?ID=${row.original.user_id.toString()}`
                )
              }
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.username}
            </span>
          );
        },
      },
      {
        accessorKey: "email",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("email")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          // return <span>{row.original.email}</span>;
          return (
            <span
              onClick={() =>
                navigate(
                  `${
                    APP_ROUTES.ADD_USERS
                  }?ID=${row.original.user_id.toString()}`
                )
              }
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.email}
            </span>
          );
        },
      },
      {
        accessorKey: "mobile",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("mobileNumber")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.mobile}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_DepartmentName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("department")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_DepartmentName_En
                : row.original.s_DepartmentName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_GateName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("gate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_GateName_En
                : row.original.s_GateName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "roleName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("role")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.roleName_En
                : row.original.roleName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "n_IsADUser",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("isADUser")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 mx-auto max-w-[175px]",
                row.original.n_IsADUser ? "bg-green-600" : "bg-yellow-500"
              )}
            >
              <p className="text-white">
                {row.original.n_IsADUser ? t("adUser") : t("notADUser")}
              </p>
            </div>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() => handleDeleteUser(row.original.user_id)}
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteUser, i18n.language, navigate, t]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (type === EXPORT_TYPES.EXCEL) {
          res = await ExportUsersForExcel(body);
        } else {
          res = await ExportUsersForPDF(body);
        }
        downloadFile(res, REPORT_NAMES.ALL_USERS);
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportUsersForExcel,
      ExportUsersForPDF,
      exportBody,
      i18n.language,
      t,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allUsers}
        columns={usersColumns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.SEARCH_USERS}
        isExportExcel
        onExport={onExport}
        setVisibleColumnKeys={setVisibleColumnKeys}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteUserModal
        userId={selectedUserId}
        isOpen={isDeleteUserModalOpen}
        onOpenChange={() => setIsDeleteUserModalOpen(false)}
      />
    </>
  );
};

export default React.memo(SearchUsersTable);
